// A device's last-sync time, shown the same way on the fleet list (WP-FL-04) and the
// device detail view (WP-FL-04a) - TP-FMP-04 NEW-K.

// Only an ISO-style date string is shown, and only when it is plausible: a number, a
// time in the future or one before 2000 is shown as "-" rather than as a real sync
// time. Date and time are both local, so a late-evening UTC time is not shown with the
// UTC date beside the local time (TP-FMP-04-TS-01-TC-0017).
export const EARLIEST_PLAUSIBLE = Date.UTC(2000, 0, 1);
export const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

// Always returns { date, time } (S3800): callers destructure the result.
export function formatUpdated(iso) {
  const none = { date: "-", time: "-" };
  if (typeof iso !== "string" || !iso.trim()) return none;
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t) || t < EARLIEST_PLAUSIBLE || t > Date.now() + FUTURE_TOLERANCE_MS) return none;
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return { date, time };
}

// How long ago the device last synced (FMP-FL-005 "last sync N min ago"). The limits:
// under 60 s "just now", then whole minutes to 59, whole hours to 23, then whole days.
// No sync record reads "never synced"; a value formatUpdated would not show (not a
// string, unparseable, before 2000, more than 5 min in the future) gives "-".
export function formatFreshness(iso, now = Date.now()) {
  if (iso == null || iso === "") return "never synced";
  if (typeof iso !== "string" || !iso.trim()) return "-";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t) || t < EARLIEST_PLAUSIBLE || t > now + FUTURE_TOLERANCE_MS) return "-";
  const seconds = Math.max(0, Math.floor((now - t) / 1000));
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
}

// Why a record is stale (FMP-AN-009): "never reported", or "no report for 34 d".
// The service decides staleness against its freshness threshold; this only words it.
export function staleReason({ lastReportedAt, dataAgeDays } = {}) {
  if (!lastReportedAt) return "never reported";
  return dataAgeDays == null ? "no recent report" : `no report for ${dataAgeDays} d`;
}

// One line: "2026-08-12 14:30 · 47 d ago", or "never synced", or "-".
export function formatLastSync(iso) {
  const fresh = formatFreshness(iso);
  if (fresh === "never synced" || fresh === "-") return fresh;
  const { date, time } = formatUpdated(iso);
  return `${date} ${time} · ${fresh}`;
}
